import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import ExecutionStatus from '@/components/surfaces/ExecutionStatus.jsx';
import PieveWordmark from '@/components/surfaces/PieveWordmark.jsx';
import { appsMetadataItems } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import resolveOauthReturn from './lib/resolveOauthReturn.js';
import toAppName from './lib/toAppName.js';
import './styles/oauthReturn.css';

/**
 * The page a provider sends the browser back to after an app sign-in (`/oauth/:appSlug/success` or
 * `/failure`, built by the API's OAuth callback). It says whether the app connected and sends the
 * person back to the connection page or conversation that started the sign-in.
 */
const OauthReturnScreen = () => {
  const navigate = useNavigate();
  const { appSlug = '', outcome = '' } = useParams();
  const [searchParams] = useSearchParams();
  const apps = useSelector(appsMetadataItems);
  const appName = toAppName({ appSlug, apps });
  const result = resolveOauthReturn({ appSlug, appName, outcome, searchParams });

  return (
    <main className="oauth-return">
      <div className="oauth-return__brand">
        <PieveWordmark height={26} />
      </div>
      <section className="oauth-return__card" aria-labelledby="oauth-return-title">
        <ExecutionStatus state={result.isConnected ? 'done' : 'failed'} label={result.isConnected ? 'Connected' : 'Not connected'} />
        <h1 id="oauth-return-title" className="oauth-return__title">
          {result.title}
        </h1>
        <p className="oauth-return__description">{result.description}</p>
        {result.providerMessage && (
          <div className="oauth-return__detail">
            <p className="oauth-return__detail-label">{result.providerMessageLabel}</p>
            <p className="oauth-return__detail-text">{result.providerMessage}</p>
          </div>
        )}
        {result.requestedScopes.length > 0 && (
          <div className="oauth-return__detail">
            <p className="oauth-return__detail-label">Access Pieve asked for</p>
            <ul className="oauth-return__scopes">
              {result.requestedScopes.map((scope) => (
                <li key={scope}>{scope}</li>
              ))}
            </ul>
          </div>
        )}
        <Button variant="primary" onClick={() => navigate(result.destination, { replace: true })}>
          {result.destinationLabel}
        </Button>
      </section>
    </main>
  );
};

export default OauthReturnScreen;
